import type { ReactNode } from 'react';
import { Bell, Briefcase, PlayCircle, Tag, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SessionTimer } from '@/components/features/SessionTimer';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import { DashboardSkeleton } from '@/components/analytics/DashboardSkeleton';
import { ErrorState } from '@/components/scenarios/ErrorState';
import { DashboardVisuals } from '@/components/dashboard/DashboardVisuals';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type KpiItem = {
  title: string;
  value: number;
  detail: string;
  foot?: string;
  icon: ReactNode;
};

function KpiStatCard({ item }: { item: KpiItem }) {
  return (
    <Card className="overflow-hidden border-primary/20 shadow-sm ring-1 ring-primary/15 transition-shadow hover:shadow-md">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"
            aria-hidden
          >
            {item.icon}
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{item.title}</p>
            <p className="text-3xl font-semibold tabular-nums tracking-tight">{item.value}</p>
            <p className="text-sm text-muted-foreground leading-snug">{item.detail}</p>
            {item.foot ? <p className="text-xs text-muted-foreground/90 leading-snug border-t border-border/60 pt-2 mt-2">{item.foot}</p> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { data, loading, error, refetch } = useDashboardSummary();

  if (loading) return <DashboardSkeleton />;
  if (error || !data) return <ErrorState error={error || 'Dashboard summary not found'} onRetry={refetch} />;

  const u = data.users;

  const overviewItems: KpiItem[] = [
    {
      title: 'Active users',
      value: u.active,
      detail:
        u.active === u.total
          ? `${u.total} total accounts`
          : `${u.active} active of ${u.total} total accounts`,
      foot: `+${u.joined_last_7_days} joined in the last 7 days`,
      icon: <Users className="h-5 w-5" />,
    },
    {
      title: 'Exercises ready',
      value: data.exercises.approved_and_prepared,
      detail: `${data.exercises.total} exercises in the catalog`,
      foot: `${data.exercises.ttx_enabled} with TTX · ${data.exercises.updated_last_7_days} updated (7d)`,
      icon: <Briefcase className="h-5 w-5" />,
    },
    {
      title: 'Runs in progress',
      value: data.runs.in_progress,
      detail: `${data.runs.completed} completed · ${data.runs.launched} launched (all time)`,
      foot: `+${data.runs.created_last_7_days} new runs in the last 7 days`,
      icon: <PlayCircle className="h-5 w-5" />,
    },
    {
      title: 'Unread notifications',
      value: data.notifications.unread,
      detail: `${data.notifications.total} notifications recorded`,
      foot: data.notifications.unread > 0 ? 'Open Notifications to clear your inbox' : 'Inbox is clear',
      icon: <Bell className="h-5 w-5" />,
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="space-y-8">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
                Cyber Range Dashboard
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Relational metrics: users, exercises, and runs
              </p>
            </div>
            <div className="text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-xl border border-border flex items-center gap-2">
              <SessionTimer />
            </div>
          </div>
        </div>

        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4"
          role="region"
          aria-label="Summary metrics"
        >
          {overviewItems.map((item) => (
            <KpiStatCard key={item.title} item={item} />
          ))}
        </div>

        <DashboardVisuals data={data} />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Quick links
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4 text-sm">
            <Link to="/notifications" className="text-primary hover:underline">
              Notifications
            </Link>
            <Link to="/scenarios" className="text-primary hover:underline">
              Scenarios
            </Link>
            <Link to="/injects" className="text-primary hover:underline">
              Injects
            </Link>
            <Link to="/phases" className="text-primary hover:underline">
              Phases
            </Link>
            <Link to="/categories" className="text-primary hover:underline">
              Categories
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
